import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
    isRouteShiftStatus,
    type RouteShiftStatus,
    syncRouteStatusFilter,
    type UnifiedShiftStatus,
} from '@apps/web/components/dashboard/shift_list_filters';
import { type DateRangePickerValue } from '@apps/web/components/ui/date_range_picker';
import { type ColumnFiltersState } from '@apps/web/lib/table';
type ShiftRouteSearch = {
    readonly date?: string;
    readonly from?: string;
    readonly to?: string;
    readonly status?: RouteShiftStatus;
    readonly clientIds?: Array<string>;
    readonly shiftId?: string;
    readonly approve?: boolean;
    readonly calloutShiftId?: string;
    readonly chatId?: string;
    readonly chatMode?: 'fullscreen';
    readonly slackWebClaim?: string;
    readonly organizationId?: string;
    readonly mode?: 'live' | 'sandbox';
};

function columnFilterStorageKey(
    organizationId: string | null | undefined,
): string {
    return `phoebe:shifts:${organizationId ?? 'anon'}:columns`;
}

function readStoredColumnFilters(
    organizationId: string | null | undefined,
): ColumnFiltersState {
    try {
        const value = localStorage.getItem(
            columnFilterStorageKey(organizationId),
        );
        return value ? (JSON.parse(value) as ColumnFiltersState) : [];
    } catch {
        return [];
    }
}

function storeColumnFilters(
    organizationId: string | null | undefined,
    filters: ColumnFiltersState,
): void {
    try {
        localStorage.setItem(
            columnFilterStorageKey(organizationId),
            JSON.stringify(filters),
        );
    } catch {
        // Storage is optional; the current route and in-memory state still work.
    }
}

export function columnFiltersForRoute(
    storedFilters: ColumnFiltersState,
    status: RouteShiftStatus | undefined,
): ColumnFiltersState {
    return syncRouteStatusFilter(storedFilters, undefined, status);
}

function routeStatusFromColumnFilters(
    filters: ColumnFiltersState,
): RouteShiftStatus | undefined {
    const statusValue = filters.find((filter) => filter.id === 'unified_status')
        ?.value as { include?: Array<UnifiedShiftStatus> } | undefined;
    const included = statusValue?.include ?? [];
    return included.length === 1 && isRouteShiftStatus(included[0])
        ? included[0]
        : undefined;
}

export function clientIdsForOrganization({
    previousOrganizationId,
    organizationId,
    routeOrganizationId,
    initialClientIds,
}: {
    readonly previousOrganizationId: string | null | undefined;
    readonly organizationId: string | null | undefined;
    readonly routeOrganizationId: string | undefined;
    readonly initialClientIds: Array<string> | undefined;
}): Array<string> {
    const switchedOrganizations =
        !!previousOrganizationId &&
        !!organizationId &&
        previousOrganizationId !== organizationId;
    const targetsCurrentOrganization = routeOrganizationId === organizationId;
    return switchedOrganizations && !targetsCurrentOrganization
        ? []
        : (initialClientIds ?? []);
}

export function useShiftListRouteFilters({
    organizationId,
    initialStatus,
    initialClientIds,
    selectedDateRange,
    selectedShiftPathId,
}: {
    readonly organizationId: string | null | undefined;
    readonly initialStatus: RouteShiftStatus | undefined;
    readonly initialClientIds: Array<string> | undefined;
    readonly selectedDateRange: DateRangePickerValue;
    readonly selectedShiftPathId?: string;
}) {
    const navigate = useNavigate();
    const routeSearch = useSearch({ strict: false }) as ShiftRouteSearch;
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() =>
        columnFiltersForRoute(
            readStoredColumnFilters(organizationId),
            initialStatus,
        ),
    );
    const [clientFilterIds, setClientFilterIds] = useState<Array<string>>(
        initialClientIds ?? [],
    );
    const previousOrganizationIdRef = useRef(organizationId);
    const previousInitialStatusRef = useRef(initialStatus);
    // This tracks the last URL-derived value, not optimistic local selection.
    const previousClientIdsKeyRef = useRef((initialClientIds ?? []).join(','));
    const initialClientIdsKey = (initialClientIds ?? []).join(',');

    useEffect(() => {
        const organizationChanged =
            previousOrganizationIdRef.current !== organizationId;
        if (organizationChanged) {
            const previousOrganizationId = previousOrganizationIdRef.current;
            previousOrganizationIdRef.current = organizationId;
            previousInitialStatusRef.current = initialStatus;
            setColumnFilters(
                columnFiltersForRoute(
                    readStoredColumnFilters(organizationId),
                    initialStatus,
                ),
            );

            const nextClientIds = clientIdsForOrganization({
                previousOrganizationId,
                organizationId,
                routeOrganizationId: routeSearch.organizationId,
                initialClientIds,
            });
            previousClientIdsKeyRef.current = nextClientIds.join(',');
            setClientFilterIds(nextClientIds);
            if (
                nextClientIds.length === 0 &&
                (initialClientIds?.length ?? 0) > 0
            ) {
                void navigate({
                    to: '/shifts',
                    search: {
                        ...routeSearch,
                        clientIds: undefined,
                    },
                    replace: true,
                });
            }
            return;
        }

        const previousStatus = previousInitialStatusRef.current;
        if (previousStatus !== initialStatus) {
            previousInitialStatusRef.current = initialStatus;
            setColumnFilters((currentFilters) =>
                syncRouteStatusFilter(
                    currentFilters,
                    previousStatus,
                    initialStatus,
                ),
            );
        }

        if (previousClientIdsKeyRef.current !== initialClientIdsKey) {
            previousClientIdsKeyRef.current = initialClientIdsKey;
            setClientFilterIds(initialClientIds ?? []);
        }
    }, [
        initialClientIds,
        initialClientIdsKey,
        initialStatus,
        navigate,
        organizationId,
        routeSearch,
        routeSearch.organizationId,
    ]);

    const navigateWithFilterSearch = useCallback(
        (search: ShiftRouteSearch) => {
            if (selectedShiftPathId) {
                void navigate({
                    to: '/shifts/$shift-id',
                    params: { 'shift-id': selectedShiftPathId },
                    search: {
                        date: search.date,
                        from: search.from,
                        to: search.to,
                        status: search.status,
                        clientIds: search.clientIds,
                        calloutShiftId: search.calloutShiftId,
                        organizationId: search.organizationId,
                        mode: search.mode,
                    },
                    replace: true,
                });
                return;
            }
            void navigate({
                to: '/shifts',
                search,
                replace: true,
            });
        },
        [navigate, selectedShiftPathId],
    );

    const updateColumnFilters = useCallback(
        (filters: ColumnFiltersState) => {
            setColumnFilters(filters);
            storeColumnFilters(organizationId, filters);
            navigateWithFilterSearch({
                ...routeSearch,
                from: selectedDateRange.from,
                to: selectedDateRange.to,
                status: routeStatusFromColumnFilters(filters),
                clientIds:
                    clientFilterIds.length > 0 ? clientFilterIds : undefined,
            });
        },
        [
            clientFilterIds,
            navigateWithFilterSearch,
            organizationId,
            routeSearch,
            selectedDateRange,
        ],
    );

    const updateClientFilter = useCallback(
        (nextClientIds: Array<string>) => {
            setClientFilterIds(nextClientIds);
            navigateWithFilterSearch({
                ...routeSearch,
                from: selectedDateRange.from,
                to: selectedDateRange.to,
                status: routeStatusFromColumnFilters(columnFilters),
                clientIds: nextClientIds.length > 0 ? nextClientIds : undefined,
            });
        },
        [
            columnFilters,
            navigateWithFilterSearch,
            routeSearch,
            selectedDateRange,
        ],
    );

    const toggleClientFilter = useCallback(
        (clientId: string) => {
            updateClientFilter(
                clientFilterIds.includes(clientId)
                    ? clientFilterIds.filter((id) => id !== clientId)
                    : [...clientFilterIds, clientId],
            );
        },
        [clientFilterIds, updateClientFilter],
    );

    return {
        columnFilters,
        clientFilterIds,
        updateColumnFilters,
        updateClientFilter,
        toggleClientFilter,
    };
}
